import { produce } from 'immer'
import { useMutation, useQueryClient } from 'react-query'

import { postStorySeen } from 'services/endpoints/post'
import {
  type StoriesData,
  type Story,
  type StoryPost
} from 'types/api/story'

interface StorySeenProps {
  activeStoryIndex: number
  story: Story
}

interface MutationContext {
  previousStoriesData?: StoriesData
  previousUserStories?: Story[]
}

export const useStorySeen = () => {
  const queryClient = useQueryClient()

  const storySeenMutation = useMutation<
    void,
    unknown,
    StorySeenProps,
    MutationContext
  >({
    mutationFn: async ({ story }) => {
      await postStorySeen(story.id)
    },

    onMutate: async props => {
      const allStoriesKey = ['allStoryPosts']
      const userStoriesKey = [
        'userStories',
        props.story.user_id
      ]

      await Promise.all([
        queryClient.cancelQueries({
          queryKey: allStoriesKey
        }),
        queryClient.cancelQueries({
          queryKey: userStoriesKey
        })
      ])

      const previousStoriesData =
        queryClient.getQueryData<StoriesData>(
          allStoriesKey
        )

      const previousUserStories =
        queryClient.getQueryData<Story[]>(
          userStoriesKey
        )

      queryClient.setQueryData<StoriesData | undefined>(
        allStoriesKey,
        oldStoriesData => {
          if (!oldStoriesData) {
            return oldStoriesData
          }

          return produce(
            oldStoriesData,
            draft => {
              const storyPosts = draft.story

              if (!Array.isArray(storyPosts)) {
                return
              }

              const storyPost = storyPosts.find(
                (item: StoryPost) =>
                  String(item.user_id) ===
                  String(props.story.user_id)
              )

              if (
                !storyPost ||
                !Array.isArray(storyPost.stories)
              ) {
                return
              }

              const targetStory =
                storyPost.stories.find(
                  item =>
                    String(item.id) ===
                    String(props.story.id)
                )

              if (!targetStory) {
                return
              }

              targetStory.seen = true

              if (
                'all_stories_seen' in storyPost
              ) {
                ;(storyPost as any).all_stories_seen =
                  storyPost.stories.every(
                    item => !!item.seen
                  )
              }
            }
          )
        }
      )

      queryClient.setQueryData<Story[] | undefined>(
        userStoriesKey,
        oldStories => {
          if (!Array.isArray(oldStories)) {
            return oldStories
          }

          return oldStories.map(story =>
            String(story.id) ===
            String(props.story.id)
              ? {
                  ...story,
                  seen: true
                }
              : story
          )
        }
      )

      return {
        previousStoriesData,
        previousUserStories
      }
    },

    onError: (
      _error,
      variables,
      context
    ) => {
      if (context?.previousStoriesData) {
        queryClient.setQueryData(
          ['allStoryPosts'],
          context.previousStoriesData
        )
      }

      if (context?.previousUserStories) {
        queryClient.setQueryData(
          [
            'userStories',
            variables.story.user_id
          ],
          context.previousUserStories
        )
      }
    },

    onSettled: (
      _data,
      _error,
      variables
    ) => {
      queryClient.invalidateQueries({
        queryKey: ['allStoryPosts']
      })

      queryClient.invalidateQueries({
        queryKey: [
          'userStories',
          variables.story.user_id
        ]
      })
    }
  })

  return {
    storySeenMutation
  }
}